// 🧑‍🤝‍🧑 useUserNames
//
// A tiny module-level name-resolution cache shared across the Messages
// surfaces (inbox rail, reading pane, header dropdown). Given a raw
// userId it returns a human display name.
//
// Names come from TWO places, and the better one comes first:
//   1. `primeUserNames` / `primeFromThreads` — what the server already
//      answered on the thread payload. Authoritative: the thread is what
//      establishes the right to see the name.
//   2. identity.getPublicUserProfile — the lazy per-id fallback, for an id
//      held with no thread around it. Privacy-filtered, so it answers for
//      few people.
//
// Design notes:
//   - Successes AND failures are cached (a failure caches a human label,
//     never the id) so a missing/private profile is never refetched in a
//     loop.
//   - Concurrent lookups for the same id are deduped via an in-flight
//     promise map.
//   - A global listener set lets every mounted `useUserNames()` consumer
//     re-render the instant a new name resolves — the list is never
//     blocked on name lookups; it paints placeholders and upgrades them.

import { useCallback, useEffect, useReducer } from 'react';
import { identity } from '../../api/identity.js';
import { fullName, personStateLabel, PERSON_STATE } from '@moksy/common/src/identity/displayName.js';

// id → resolved display name (string). Presence here means "settled".
const cache = new Map();
// id → Promise, present only while a lookup is outstanding.
const inflight = new Map();
// Subscribers (one per mounted hook) pinged whenever the cache grows.
const listeners = new Set();

const notify = () => { listeners.forEach((fn) => { try { fn(); } catch { /* noop */ } }); };

/**
 * ⚠️ THIS USED TO BE `shortenId`, AND IT PUT AN ID IN THE NAME SLOT.
 *
 * An id we could not resolve was rendered as "6a0568…79be", with the
 * monogram "6", in the place a person's name goes — in the header dropdown
 * and down the /messages rail. It reads as a real contact, it cannot be
 * searched for, and several of them are indistinguishable from each other.
 * The repo already has one rule about this (`projectName`, `PersonRef`):
 * derive a name, never show a bare id.
 *
 * The words come from `packages/common/src/identity`, shared with the
 * server that decides WHICH absence this is, so the rail, the reading pane
 * and the moderation feed cannot drift into three vocabularies for it.
 *
 * `shortenId` had no callers outside this file — checked before deleting
 * it, since a debug or admin surface would have been a fair reason to keep
 * it. If one ever wants the id, it should ask for the id, not for a name.
 */
export const unresolvedLabel = (state) => personStateLabel(state);

// What a name slot holds while a lookup is still in the air. Deliberately
// not a placeholder sentence: "Unknown participant" flashing into a real
// name half a second later says something false in between.
const RESOLVING = '…';

// Derive a display name from a public profile, tolerating the various
// shapes the identity service can return (name fields live under
// `about`, but the schema is passthrough so top-level fallbacks exist).
export const deriveName = (profile) => {
  if (!profile || typeof profile !== 'object') return '';
  // 👤 The shared rule, not a fourth copy of it.
  //
  // This chain ended at the EMAIL ADDRESS, so a conversation with somebody
  // whose profile carried no name showed their email in the thread list —
  // to whoever they were talking to, on a screen either of them might be
  // sharing. `fullName` stops at the username and never reaches a
  // credential. See packages/common/src/identity/displayName.
  return fullName(profile, '');
};

// Ids asked for since the last flush, awaiting the next batch request.
const pending = new Set();
// Handle for the scheduled flush (null when none is pending).
let flushTimer = null;
// Coalescing window: asks that land within this many ms share one HTTP
// round-trip. Short enough to feel instant, long enough for a whole
// inbox render's worth of ids to pile up into a single request.
const BATCH_DELAY_MS = 40;

// Single-id fallback — the original per-id resolution path. Used only
// when the batch endpoint errors, so a transient batch failure never
// leaves a thread permanently unlabeled. Caches success AND failure.
const resolveSingle = (key) => identity.getPublicUserProfile(key)
  .then((profile) => { cache.set(key, deriveName(profile) || unresolvedLabel(PERSON_STATE.UNKNOWN)); })
  .catch(() => { cache.set(key, unresolvedLabel(PERSON_STATE.UNKNOWN)); })
  .finally(() => { inflight.delete(key); });

// Drain `pending` into ONE batch request. Every requested id is settled
// exactly once: to its derived name if present in the response, or to
// "Unknown participant" otherwise (unknown/private/deleted profiles are
// omitted by the server) — so a missing id caches and is never refetched.
// A thread participant should never reach here unresolved: the server
// names them on the payload, and `primeFromThreads` seeds that first.
const flush = () => {
  flushTimer = null;
  const ids = Array.from(pending);
  pending.clear();
  if (!ids.length) return;

  identity.getPublicUserProfiles(ids)
    .then((profiles) => {
      const byId = new Map();
      (Array.isArray(profiles) ? profiles : []).forEach((profile) => {
        const pid = String(profile?.id || '');
        if (pid) byId.set(pid, profile);
      });
      ids.forEach((key) => {
        const profile = byId.get(key);
        cache.set(key, (profile && deriveName(profile)) || unresolvedLabel(PERSON_STATE.UNKNOWN));
        inflight.delete(key);
      });
      notify();
    })
    .catch(() => {
      // Batch failed wholesale — degrade gracefully to single-id fetches.
      Promise.allSettled(ids.map((key) => resolveSingle(key)))
        .finally(() => notify());
    });
};

// Kick off a lazy resolution for `id` unless it's already settled or in
// flight. Safe to call from render — it never sets state synchronously.
// The id is reserved in `inflight` immediately so concurrent asks dedupe,
// then enqueued for the next coalesced batch flush.
export const requestUserName = (id) => {
  const key = String(id || '');
  if (!key || cache.has(key) || inflight.has(key)) return;
  inflight.set(key, true);
  pending.add(key);
  if (!flushTimer) flushTimer = setTimeout(flush, BATCH_DELAY_MS);
};

// Synchronous peek — the resolved name, or undefined if not yet settled.
export const peekUserName = (id) => cache.get(String(id || ''));

/**
 * Seed the cache with names the SERVER has already answered.
 *
 * Threads arrive carrying `participantNames` / `participantStates`, resolved
 * server-side because the thread is what establishes the right to see them.
 * Every other name slot on the page — the message author in the reading
 * pane, the roster, a mention — went and asked the PUBLIC profile endpoint
 * about the same ids and mostly got nothing, so one screen could show a name
 * in the rail and an id three inches to the right of it.
 *
 * Server truth overwrites anything already cached, including a placeholder
 * the client settled on earlier: it is the better answer, not a duplicate.
 */
export const primeUserNames = (entries) => {
  if (!entries || typeof entries !== 'object') return;
  let changed = false;
  for (const [id, name] of Object.entries(entries)) {
    const key = String(id || '');
    const value = String(name || '');
    if (!key || !value || cache.get(key) === value) continue;
    cache.set(key, value);
    inflight.delete(key);
    pending.delete(key);
    changed = true;
  }
  if (changed) notify();
};

/**
 * Seed from a list of threads in one pass — names first, then the label for
 * everyone the server could not name.
 */
export const primeFromThreads = (threads) => {
  const seed = {};
  for (const thread of (Array.isArray(threads) ? threads : [])) {
    Object.assign(seed, thread?.participantNames || {});
    for (const [id, state] of Object.entries(thread?.participantStates || {})) {
      if (!seed[id]) seed[id] = unresolvedLabel(state);
    }
  }
  primeUserNames(seed);
};

// The participant ids that make up "the other side" of a thread — self
// and the literal 'system' actor excluded. Prefers the server's
// `otherParticipants` projection (already self/system-filtered); falls
// back to the raw `participants` list used by the moderation feed.
export const counterpartIds = (thread, myUserId) => {
  const me = String(myUserId || '');
  const clean = (list) => (Array.isArray(list) ? list : [])
    .map((p) => (typeof p === 'string' ? p : (p?.userId || p?.id || p?.email || '')))
    .map((p) => String(p || ''))
    .filter((p) => p && p !== 'system' && p !== me);

  const others = clean(thread?.otherParticipants);
  if (others.length) return others;
  return clean(thread?.participants);
};

// A joined counterpart label from an array of resolved names. One name
// for a 1:1 thread; "Name, Name…" once it's a group.
export const joinNames = (names) => {
  const clean = names.filter(Boolean);
  if (!clean.length) return '';
  if (clean.length > 2) return `${clean.slice(0, 2).join(', ')}…`;
  return clean.join(', ');
};

// React hook: subscribe to the shared cache and expose lookups.
export function useUserNames() {
  const [, bump] = useReducer((n) => n + 1, 0);

  useEffect(() => {
    listeners.add(bump);
    return () => { listeners.delete(bump); };
  }, []);

  // Resolve a single id to a name, lazily fetching on first ask.
  const nameFor = useCallback((id, { loadingLabel } = {}) => {
    const key = String(id || '');
    if (!key) return '';
    const cached = cache.get(key);
    if (cached) return cached;
    requestUserName(key);
    return loadingLabel || RESOLVING;
  }, []);

  // Resolve a thread's counterpart set to a single display label.
  //
  // `thread.participantNames` comes from the server and wins, because it is
  // the only source that can name someone whose profile is not public. The
  // per-id lookup below asks the PUBLIC profile endpoint, which filters on
  // settings.privacy.profilePublic — a flag defaulting to false — so it
  // returned nothing for almost everyone and the inbox showed shortened ids
  // to people who were already in the conversation.
  //
  // Kept as the fallback rather than removed: it still answers for surfaces
  // that hold an id with no thread around it, and it means an older server
  // that does not send participantNames behaves exactly as before.
  const counterpartLabel = useCallback((thread, myUserId, { loadingLabel } = {}) => {
    const ids = counterpartIds(thread, myUserId);
    if (!ids.length) return thread?.title || 'Conversation';
    const supplied = thread?.participantNames || {};
    // `participantStates` is the server saying an id names nobody, and WHY:
    // an erased account is a former member, an id that was never issued is
    // an unknown participant. Without it the row fell through to a lookup
    // that can never succeed and ended at the id itself.
    const states = thread?.participantStates || {};
    return joinNames(ids.map((id) => (
      supplied[id] || (states[id] ? unresolvedLabel(states[id]) : nameFor(id, { loadingLabel }))
    ))) || (thread?.title || 'Conversation');
  }, [nameFor]);

  return { nameFor, counterpartLabel };
}
